import React, { useState, useEffect, useRef } from 'react';
import { Box, IconButton, TextField, Popover, Button } from '@mui/material';
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatColorText,
  Link,
  Close
} from '@mui/icons-material';

const InlineTextEditor = ({ 
  isVisible, 
  position, 
  selectedText, 
  onFormat, 
  onClose,
  onAddLink 
}) => {
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [textColor, setTextColor] = useState('#333333');
  const colorAnchorRef = useRef(null);

  useEffect(() => {
    if (!isVisible) {
      setShowColorPicker(false);
      setShowLinkDialog(false);
      setLinkUrl('');
    }
  }, [isVisible]);

  if (!isVisible || !position) return null;

  const handleColorChange = (color) => {
    setTextColor(color);
    onFormat('color', color);
    setShowColorPicker(false);
  };

  const handleAddLink = () => {
    if (linkUrl.trim()) {
      onAddLink(linkUrl.trim());
      setShowLinkDialog(false);
      setLinkUrl('');
      onClose();
    }
  };

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          left: Math.max(10, position.x - 100),
          top: Math.max(10, position.y - 60),
          backgroundColor: '#2c3e50',
          borderRadius: '8px',
          padding: '4px',
          display: 'flex',
          alignItems: 'center',
          gap: '2px',
          zIndex: 999999,
          boxShadow: '0 8px 25px rgba(0,0,0,0.4)',
          transform: 'translateX(-50%)',
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '100%',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 0,
            height: 0,
            borderLeft: '6px solid transparent',
            borderRight: '6px solid transparent',
            borderTop: '6px solid #2c3e50',
          },
        }}
      >
        <IconButton
          size="small"
          onClick={() => onFormat('fontWeight', 'bold')}
          sx={{ color: '#ecf0f1', padding: '4px' }}
        >
          <FormatBold fontSize="small" />
        </IconButton>

        <IconButton
          size="small"
          onClick={() => onFormat('fontStyle', 'italic')}
          sx={{ color: '#ecf0f1', padding: '4px' }}
        >
          <FormatItalic fontSize="small" />
        </IconButton>

        <IconButton
          size="small"
          onClick={() => onFormat('textDecoration', 'underline')}
          sx={{ color: '#ecf0f1', padding: '4px' }}
        >
          <FormatUnderlined fontSize="small" />
        </IconButton>

        <IconButton
          ref={colorAnchorRef}
          size="small"
          onClick={() => setShowColorPicker(true)}
          sx={{ color: '#ecf0f1', padding: '4px' }}
        >
          <FormatColorText fontSize="small" />
        </IconButton>

        <IconButton
          size="small"
          onClick={() => setShowLinkDialog(true)}
          sx={{ color: '#ecf0f1', padding: '4px' }}
        >
          <Link fontSize="small" />
        </IconButton>

        <IconButton
          size="small"
          onClick={onClose}
          sx={{ color: '#ecf0f1', padding: '4px' }}
        >
          <Close fontSize="small" />
        </IconButton>
      </Box>

      {/* Color Picker Popover */}
      <Popover
        open={showColorPicker}
        anchorEl={colorAnchorRef.current}
        onClose={() => setShowColorPicker(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', maxWidth: 200 }}>
            {[
              '#000000', '#333333', '#666666', '#999999', '#cccccc',
              '#ff0000', '#ff6600', '#ffcc00', '#00ff00', '#0066ff',
              '#6600ff', '#ff00ff', '#00ffff', '#ffff00', '#ffffff'
            ].map(color => (
              <Box
                key={color}
                sx={{
                  width: 24,
                  height: 24,
                  backgroundColor: color,
                  border: '1px solid #ddd',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  '&:hover': {
                    transform: 'scale(1.1)',
                  }
                }}
                onClick={() => handleColorChange(color)}
              />
            ))}
          </Box>
          <input
            type="color"
            value={textColor}
            onChange={(e) => handleColorChange(e.target.value)}
            style={{ width: '100%', height: '32px', border: 'none', borderRadius: '4px' }}
          />
        </Box>
      </Popover>

      {/* Link Dialog */}
      <Popover
        open={showLinkDialog}
        anchorEl={colorAnchorRef.current}
        onClose={() => setShowLinkDialog(false)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box sx={{ p: 2, minWidth: 300 }}>
          <TextField
            fullWidth
            label="Enter URL"
            value={linkUrl}
            onChange={(e) => setLinkUrl(e.target.value)}
            placeholder="https://example.com"
            size="small"
            sx={{ mb: 2 }}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                handleAddLink();
              }
            }}
          />
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button 
              size="small" 
              onClick={() => setShowLinkDialog(false)}
            >
              Cancel
            </Button>
            <Button 
              size="small" 
              variant="contained" 
              onClick={handleAddLink}
              disabled={!linkUrl.trim()}
            >
              Add Link
            </Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default InlineTextEditor; 